import { buildPlanViewMesh } from "@/components/Project/FoundationExcavation/buildPlanViewMesh";
import store from "@/store";
import { transformDrawingToSiteEastNorth } from "@/utils/foundationExcavationCoord";
import { meshDataToFlat } from "@/utils/foundationExcavationMesh";
import {
	type CoordinateSetting,
	type ModelSnapshot,
	computeUnifiedExcavationFromSnapshot,
} from "@earthbrain/foundation-excavation-sdk";
import { computePlanViewFromSnapshot } from "@earthbrain/foundation-excavation-sdk/data-io";
import { md5 } from "hash-wasm";
import { type IObject, ObjectType } from "./types";

export const FOUNDATION_EXCAVATION_VERSION = 2 as const;

export interface IMeta {
	readonly name: string;
	readonly createdAt: string;
}

export interface IFoundationExcavationParams {
	readonly id?: string;
	// coordinateSetting: site coordinates of the two SDK grid intersections
	// (basePoint = reference point 1 / translation, directionPoint = reference point 2 / orientation)
	readonly coordinateSetting: CoordinateSetting;
	readonly meta: IMeta;
	readonly snapshot: ModelSnapshot;
}

export interface IFoundationExcavationEnvelope {
	readonly version: typeof FOUNDATION_EXCAVATION_VERSION;
	readonly type: ObjectType.FOUNDATION_EXCAVATION;
	readonly id: string;
	readonly cid: string;
	readonly name: string;
	readonly coordinateSetting: CoordinateSetting;
	readonly meta: IMeta;
	readonly snapshot: ModelSnapshot;
	readonly md5: string;
}

export class FoundationExcavationObject implements IObject {
	readonly type: ObjectType.FOUNDATION_EXCAVATION =
		ObjectType.FOUNDATION_EXCAVATION;
	id: string;
	md5 = "";
	name: string;
	isShow = false;
	isOpen = false;
	isLoaded = false;
	associatedTerrain = { id: 0, terrainType: "" };
	coordinateSetting: CoordinateSetting;
	meta: IMeta;
	snapshot: ModelSnapshot;

	constructor(params: IFoundationExcavationParams) {
		this.id = params.id ?? crypto.randomUUID();
		this.coordinateSetting = params.coordinateSetting;
		this.meta = params.meta;
		this.snapshot = params.snapshot;
		this.name = params.meta.name;
	}

	static fromEnvelope(
		envelope: IFoundationExcavationEnvelope,
	): FoundationExcavationObject {
		const obj = new FoundationExcavationObject({
			id: envelope.id,
			coordinateSetting: envelope.coordinateSetting,
			meta: envelope.meta,
			snapshot: envelope.snapshot,
		});
		obj.name = envelope.name;
		obj.md5 = envelope.md5;
		return obj;
	}

	async setVisibility(_visible: boolean): Promise<boolean> {
		// no-op: 3D rendering deferred to a separate phase.
		// IObject reserves `false` for "blocked".
		return true;
	}

	async update(
		data: Partial<IFoundationExcavationParams>,
		_updateInViewer?: boolean,
	): Promise<void> {
		if (data.coordinateSetting) this.coordinateSetting = data.coordinateSetting;
		if (data.meta) {
			this.meta = data.meta;
			this.name = data.meta.name;
		}
		if (data.snapshot) this.snapshot = data.snapshot;
	}

	startEdit(): void {
		// Open the re-edit modal. Lock / inProgressMode / objectUnderEdit are
		// owned by object/startEdit, which calls this after acquiring the lock.
		store.commit("dialogs/showDialog", "propertyDialogFoundationExcavation");
	}

	async endEdit(): Promise<void> {
		// Close the re-edit modal. Called by object/endEdit during teardown.
		store.commit("dialogs/closeDialog", "propertyDialogFoundationExcavation");
	}

	deleteObject(): void {
		// no-op: state removal is handled by the object/deleteObject action
		// (setObjects). This method is viewer/3D teardown only, deferred to a
		// later phase. Edit-time delete cleanup is unreachable because the modal
		// blocks list operations.
	}

	async getGeometry(): Promise<{ vertices: number[]; indices: number[] }> {
		const mesh = await computeUnifiedExcavationFromSnapshot(this.snapshot);
		if (!mesh) return { vertices: [], indices: [] };
		const { vertices: localVertices, indices } = meshDataToFlat(mesh);
		// Place SDK drawing mesh at the field position defined by the 2-point setting.
		const vertices = transformDrawingToSiteEastNorth(
			localVertices,
			this.snapshot.grid,
			this.coordinateSetting,
		);
		return { vertices, indices };
	}

	getPlanViewGeometry(): { vertices: number[]; indices: number[] } {
		const planView = computePlanViewFromSnapshot(this.snapshot);
		const { vertices: localVertices, indices } = buildPlanViewMesh(planView);
		const vertices = transformDrawingToSiteEastNorth(
			localVertices,
			this.snapshot.grid,
			this.coordinateSetting,
		);
		return { vertices, indices };
	}

	fitToView(): void {
		// no-op: 3D rendering deferred to a separate phase
	}

	async toJSON() {
		const body = {
			version: FOUNDATION_EXCAVATION_VERSION,
			type: this.type,
			id: this.id,
			cid: this.id,
			name: this.name,
			coordinateSetting: this.coordinateSetting,
			meta: this.meta,
			snapshot: this.snapshot,
		};
		let hash: string;
		try {
			hash = await md5(JSON.stringify(body));
		} catch (error) {
			console.error("Failed to generate MD5 hash:", error);
			hash = "M85FailedToGenerateMD5Hash";
		}
		return { ...body, md5: hash };
	}

	/**
	 * Build an envelope for duplication: new id/cid, name suffixed with " copy",
	 * all other data copied as-is.
	 */
	async toDuplicateJSON(): Promise<IFoundationExcavationEnvelope> {
		const newId = crypto.randomUUID();
		const newName = `${this.name} copy`;
		const body = {
			version: FOUNDATION_EXCAVATION_VERSION,
			type: this.type,
			id: newId,
			cid: newId,
			name: newName,
			coordinateSetting: this.coordinateSetting,
			// keep meta.name in sync with name (constructor derives name from meta.name)
			meta: { ...this.meta, name: newName },
			snapshot: this.snapshot,
		};
		let hash: string;
		try {
			hash = await md5(JSON.stringify(body));
		} catch (error) {
			console.error("Failed to generate MD5 hash:", error);
			hash = "M85FailedToGenerateMD5Hash";
		}
		return { ...body, md5: hash };
	}
}
